import {getFormResultConfig, getFormResultAnswers, getFormResultDate, getStoredFormAnswerLabel} from "./formResultHelpers.js";
export function getSubscaleProfileConfigErrors(config = {}) {
	const profile = config.subscaleProfile
	if (!profile) return []
	if (!Array.isArray(profile.items) || !Array.isArray(profile.modes) || !Array.isArray(profile.anchors)) return ["Invalid rating profile configuration"]
	if (!Number.isInteger(profile.ratingMax) || profile.ratingMax < 3 || profile.ratingMax > 10 || profile.anchors.length !== profile.ratingMax) return ["Rating anchors must match the scale maximum"]
	if (profile.anchors.some((anchor, index) => !anchor.id || !anchor.title || anchor.value !== index + 1) || new Set(profile.anchors.map(anchor => anchor.id)).size !== profile.anchors.length) return ["Invalid rating anchors"]
	if (!profile.items.every(item => item.id && item.text && Array.isArray(item.modeIds)) || !profile.modes.every(mode => mode.id && mode.title)) return ["Every question and mode needs an ID and title"]
	const itemIds = profile.items.map(item => item.id)
	const modeIds = profile.modes.map(mode => mode.id)
	if (new Set(itemIds).size !== itemIds.length || new Set(modeIds).size !== modeIds.length) return ["Question and mode IDs must be unique"]
	if (profile.items.some(item => item.modeIds.some(id => !modeIds.includes(id)))) return ["A question refers to a removed mode"]
	if (!["mean", "sum", "thresholdSum"].includes(profile.scoringMethod)) return ["Invalid scoring method"]
	if (profile.scoringMethod === "thresholdSum") {
		if (!Number.isInteger(profile.scoringThreshold) || profile.scoringThreshold < 1 || profile.scoringThreshold > profile.ratingMax) return ["Invalid scoring threshold"]
		if (profile.items.some(item => !Number.isInteger(Number(item.weight)) || Number(item.weight) < 1)) return ["Every question needs a positive whole-number scoring weight"]
		if (profile.modes.some(mode => !Array.isArray(mode.bandUpper) || mode.bandUpper.length !== 3 || mode.bandUpper.some((value, index) => !Number.isInteger(Number(value)) || Number(value) < 0 || (index > 0 && Number(value) <= Number(mode.bandUpper[index - 1]))))) return ["Invalid interpretation bands"]
	}
	return []
}

function resolveRating(value, profile) {
	if (typeof value === "string") return profile.anchors?.find(anchor => anchor.id === value)?.value
	return value
}

function getBand(score, mode) {
	if (score == null || !Array.isArray(mode.bandUpper)) return null
	const index = mode.bandUpper.findIndex(upper => score <= Number(upper))
	return ["Low", "Medium", "High", "Very High"][index < 0 ? 3 : index]
}

export function getSubscaleProfileResult(answers = {}, config = {}) {
	const profile = config.subscaleProfile || {}
	const items = profile.items || []
	const missingItems = []
	const invalidItems = []
	for (const item of items) {
		const value = answers[item.id]
		const rating = resolveRating(value, profile)
		if (value === "" || value === null || value === undefined) missingItems.push(item.id)
		else if (!Number.isInteger(rating) || rating < 1 || rating > profile.ratingMax) invalidItems.push(item.id)
	}
	const isComplete = items.length > 0 && !missingItems.length && !invalidItems.length
	const scores = {}
	const maxima = {}
	const bands = {}
	for (const mode of profile.modes || []) {
		const assigned = items.filter(item => item.modeIds.includes(mode.id))
		const sum = assigned.reduce((total, item) => {
			const rating = resolveRating(answers[item.id], profile) || 0
			return total + (profile.scoringMethod === "thresholdSum" ? (rating >= profile.scoringThreshold ? rating * Number(item.weight || 1) : 0) : rating)
		}, 0)
		maxima[mode.id] = profile.scoringMethod === "mean" ? profile.ratingMax : assigned.reduce((total, item) => total + profile.ratingMax * (profile.scoringMethod === "thresholdSum" ? Number(item.weight || 1) : 1), 0)
		scores[mode.id] = isComplete && assigned.length ? (profile.scoringMethod === "mean" ? sum / assigned.length : sum) : null
		bands[mode.id] = profile.scoringMethod === "thresholdSum" ? getBand(scores[mode.id], mode) : null
	}
	return {missingItems, invalidItems, answeredCount: items.length - missingItems.length - invalidItems.length, itemCount: items.length, isComplete, scores, maxima, bands}
}

export function getSubscaleProfilePayloadErrors(payload = {}, config = {}) {
	const errors = getSubscaleProfileConfigErrors(config)
	if (errors.length) return errors
	const result = getSubscaleProfileResult(payload.answers || {}, config)
	if (result.invalidItems.length) return ["Invalid questionnaire rating"]
	if (!result.isComplete) return ["Complete every questionnaire item before submitting"]
	return []
}

export function getSubscaleProfileResultView(config, result = {}, priorResults = []) {
	config = getFormResultConfig(config, result)
	const profile = config.subscaleProfile || {items: [], modes: [], anchors: []}
	const answers = getFormResultAnswers(result)
	const report = getSubscaleProfileResult(answers, config)
	const previousProfile = getFormResultConfig(undefined, result).subscaleProfile || {}
	const activeIds = new Set((profile.items || []).map(item => item.id))
	const candidates = [...(profile.archivedItems || []), ...(previousProfile.archivedItems || []), ...(previousProfile.items || [])]
	const archivedItems = [...new Map(candidates.filter(item => !activeIds.has(item.id) && answers[item.id] !== undefined && answers[item.id] !== null && answers[item.id] !== "").map(item => [item.id, item])).values()]
	const groupedModes = []
	for (const mode of profile.modes || []) {
		const title = mode.group || "Other modes"
		let group = groupedModes.find(item => item.title === title)
		if (!group) {group = {title, modes: []}; groupedModes.push(group)}
		group.modes.push(mode)
	}
	const availablePriorRows = priorResults.filter(prior => (!result.id || prior.id !== result.id) && prior.toolType === result.toolType).map((prior, index) => ({id: prior.id || index, date: getFormResultDate(prior, "administrationDate"), context: getFormResultConfig(undefined, prior).subscaleProfile?.purpose || "", scores: getSubscaleProfileResult(getFormResultAnswers(prior), config).scores}))
	const selectedIds = result.enginePayload?.privatePractitioner?.selectedPriorIds || []
	const priorRows = profile.selectComparisons ? availablePriorRows.filter(row => selectedIds.includes(row.id)) : availablePriorRows
	const dateLabel = getFormResultDate(result, "administrationDate") || "Date not recorded"
	const dueStatus = !/^\d{4}-\d{2}-\d{2}$/.test(dateLabel) ? "timing unavailable" : dateLabel > profile.dueDate ? "completed after due date" : "completed by due date"
	const anchors = (profile.anchors || []).map(anchor => ({...anchor, id: anchor.value}))
	const previousAnchors = (previousProfile.anchors || []).map(anchor => ({...anchor, id: anchor.value}))
	const answerLabels = Object.fromEntries(Object.values(answers).map(value => [value, typeof value === "string" ? (profile.anchors || []).find(anchor => anchor.id === value)?.title || "Unanswered" : getStoredFormAnswerLabel(anchors, value, previousAnchors)]))
	return {config, profile, answers, report, archivedItems, groupedModes, availablePriorRows, priorRows, dateLabel, dueStatus, answerLabels}
}
